import Editor from '@monaco-editor/react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { Badge } from '@/components/ui/badge';
import { popModal } from '@/redux/modals/actions';
import type { ToolInvocation } from '@/shared/types/tryIt';

import styles from '../DetailsSection.module.scss';

type ToolInvocationRowProps = {
  invocation: ToolInvocation;
  serverName: string;
  serverLink: string;
  isSynthetic: boolean;
  isExpanded: boolean;
  onToggle: () => void;
};

export const ToolInvocationRow = ({
  invocation,
  serverName,
  serverLink,
  isSynthetic,
  isExpanded,
  onToggle,
}: ToolInvocationRowProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const jsonData = {
    arguments: invocation.arguments,
    result: invocation.result,
    meta: invocation.meta,
  };
  const jsonString = JSON.stringify(jsonData, null, 2);

  return (
    <div className="flex flex-col gap-1">
      <div
        className="flex cursor-pointer items-center gap-2"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onToggle();
          }
        }}
      >
        <span className="max-w-[200px] truncate text-sm text-foreground" title={invocation.name}>
          {invocation.name}
        </span>

        <span className="text-sm text-foreground-muted">
          {isSynthetic ? (
            serverName
          ) : (
            <>
              {t('try_it.from_server')}{' '}
              <NavLink to={serverLink} className={styles.link} onClick={() => dispatch(popModal())}>
                {serverName}
              </NavLink>
            </>
          )}
        </span>

        {!invocation.succeeded && (
          <Badge type="secondary" flavour="destructive">
            {t('try_it.failed')}
          </Badge>
        )}

        <div className="ml-auto text-foreground-muted">
          {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
        </div>
      </div>

      {isExpanded && (
        <div className="relative mt-2 overflow-hidden rounded-lg border border-border">
          <CopyButton data={jsonString} iconSize={14} className="absolute top-2 right-2 z-10" />
          <Editor
            value={jsonString}
            language="json"
            theme="vs-dark"
            height="320px"
            options={{
              readOnly: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              lineNumbers: 'off',
              folding: true,
              fontSize: 12,
              automaticLayout: true,
            }}
          />
        </div>
      )}
    </div>
  );
};
